import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Circle,
} from 'lucide-react';
import type { WbInstantiateEvent } from '@/lib/api/services/websiteBuilder';

interface InstantiateProgressProps {
  /** Accumulated SSE events in arrival order. The component derives the
   *  visible checklist from this array — order matters and duplicates are
   *  rendered (a multi-page template fires saved_page N times). */
  events: WbInstantiateEvent[];
  /** Streaming = SSE is still open. Done = terminal `done` event seen.
   *  Error = terminal error event OR network-level failure surfaced by
   *  the SSE wrapper. */
  status: 'streaming' | 'done' | 'error';
  errorMessage?: string;
  /** Called when the user clicks "Try again" (only rendered in error state). */
  onRetry?: () => void;
  /** Called when the user clicks "Pick a different template" OR presses ESC
   *  while in error state (ESC during streaming is deliberately ignored —
   *  the work is in-flight). */
  onPickDifferent?: () => void;
}

interface ChecklistItem {
  key: string;
  label: string;
  /** done = green check, active = spinner, pending = grey dot, error = red */
  state: 'done' | 'active' | 'pending' | 'error';
  /** Optional small subdued hint shown under the label (used for the
   *  ai_status: 'ok' vs 'fallback_*' affordance on saved_page). */
  hint?: string;
}

/**
 * Translate the raw SSE event stream into a chronological checklist. The
 * latest event is "active" (spinner) when status === 'streaming'; everything
 * before it is "done" (check). When status === 'done' we render an extra
 * "Opening editor..." item so the user has a beat of feedback before the
 * parent navigates. When status === 'error' the latest entry is "error" and
 * every prior entry stays "done" — so the user sees how far we got.
 */
function buildChecklist(
  events: WbInstantiateEvent[],
  status: 'streaming' | 'done' | 'error',
): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  let idx = 0;
  for (const ev of events) {
    idx += 1;
    switch (ev.type) {
      case 'fetching_template':
        items.push({ key: `t${idx}`, label: 'Fetching template…', state: 'done' });
        break;
      case 'merging_ai_knowledge':
        items.push({ key: `t${idx}`, label: 'Saving your business info…', state: 'done' });
        break;
      case 'creating_site':
        items.push({
          key: `t${idx}`,
          label: `Creating site: "${ev.site.slug}"…`,
          state: 'done',
        });
        break;
      case 'personalizing_page':
        items.push({
          key: `t${idx}`,
          label: `Personalizing ${ev.page_slug} page…`,
          state: 'done',
        });
        break;
      case 'saved_page': {
        const aiOk = ev.ai_status === 'ok';
        items.push({
          key: `t${idx}-${ev.page_id}`,
          label: `Saved ${ev.page_slug} page`,
          state: 'done',
          hint: aiOk
            ? undefined
            : 'AI personalization unavailable — using your business info directly.',
        });
        break;
      }
      case 'done':
        items.push({ key: `t${idx}`, label: 'Opening editor…', state: 'done' });
        break;
      case 'error':
        items.push({
          key: `t${idx}`,
          label: ev.message || 'Something went wrong',
          state: 'error',
        });
        break;
      // Forward-compat: unknown event shapes are silently skipped (spec §11.22).
    }
  }

  // Flip the latest "done" into "active" while streaming, so the user sees the
  // spinner on the step that's actually in flight. On `done` we keep the last
  // item ("Opening editor…") as done because the parent will navigate next.
  if (status === 'streaming' && items.length > 0) {
    const last = items[items.length - 1];
    if (last.state === 'done') last.state = 'active';
  }
  return items;
}

/**
 * Full-screen overlay shown while POST /wb/templates/:id/instantiate is
 * streaming. Renders the SSE events as a chronological checklist with
 * spinner-on-active-step + green-check-on-done. Auto-navigation lives in
 * the parent — this component just renders feedback.
 *
 * Keyboard contract (spec §7 + edge case §11.16):
 *  - ESC while streaming → IGNORED. The work is mid-flight; cancelling here
 *    would leave a half-instantiated site. Server-side watchdog handles
 *    actual cancellation.
 *  - ESC while error    → fires onPickDifferent. Safer than onRetry because
 *    retry costs OpenAI tokens; pick-different is free.
 *  - ESC while done     → IGNORED. The parent navigates within ~500ms; ESC
 *    interrupting that is a no-op race we don't need to support.
 *
 * Accessibility: role="dialog" + aria-live="polite" so screen readers
 * announce each event as it lands.
 */
export const InstantiateProgress: React.FC<InstantiateProgressProps> = ({
  events,
  status,
  errorMessage,
  onRetry,
  onPickDifferent,
}) => {
  // ESC handling — gated on status. The handler is added to `document` so it
  // works regardless of which child holds focus (none of our content is
  // focusable while streaming).
  useEffect(() => {
    if (status !== 'error') return; // streaming + done both ignore ESC
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Safer default: drop back to the gallery instead of silently
        // re-firing the API call. The user can click Retry explicitly if
        // they want to spend the tokens again.
        onPickDifferent?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [status, onPickDifferent]);

  const items = buildChecklist(events, status);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-live="polite"
      aria-modal="true"
      data-testid="wb-instantiate-overlay"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl p-6">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            {status === 'error' ? 'Could not create your site' : 'Personalizing your site…'}
          </h2>
          {status !== 'error' && (
            <p className="text-xs text-gray-500 mt-1">
              This takes a few seconds — we're fetching your template and
              rewriting the copy for your business.
            </p>
          )}
        </div>

        <ul className="space-y-3" data-testid="wb-instantiate-checklist">
          {items.map((item) => (
            <li
              key={item.key}
              className="flex items-start gap-3"
              data-testid={`wb-instantiate-step-${item.state}`}
            >
              <ItemIcon state={item.state} />
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm ${
                    item.state === 'error'
                      ? 'text-red-700'
                      : item.state === 'pending'
                      ? 'text-gray-400'
                      : item.state === 'active'
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-700'
                  }`}
                >
                  {item.label}
                </div>
                {item.hint && (
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {item.hint}
                  </div>
                )}
              </div>
            </li>
          ))}
          {items.length === 0 && status === 'streaming' && (
            <li
              className="flex items-center gap-3 text-sm text-gray-500"
              data-testid="wb-instantiate-step-active"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting…
            </li>
          )}
        </ul>

        {status === 'error' && (
          <div
            className="mt-5 rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-700"
            data-testid="wb-instantiate-error-block"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                {errorMessage || 'Something went wrong. Please try again.'}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onPickDifferent}
                data-testid="wb-instantiate-pick-different"
              >
                Pick a different template
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={onRetry}
                data-testid="wb-instantiate-retry"
              >
                Try again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ItemIcon: React.FC<{ state: ChecklistItem['state'] }> = ({ state }) => {
  if (state === 'done') {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />;
  }
  if (state === 'active') {
    return <Loader2 className="h-4 w-4 text-purple-600 animate-spin mt-0.5 shrink-0" />;
  }
  if (state === 'error') {
    return <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />;
  }
  return <Circle className="h-4 w-4 text-gray-300 mt-0.5 shrink-0" />;
};
